"use client";

import React from "react";
import { Maximize2, ArrowRight, ShoppingBag } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogClose } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
const galleryItems = [{
  id: 1,
  title: "The Silk Study",
  description: "An intimate exploration of light and texture. This session captures the effortless grace of silk against skin, utilizing soft morning rays to highlight natural beauty. A testament to the quiet power of vulnerability.",
  src: "https://warm-lionfish.demandflow.net/wp-content/uploads/2026/04/image_6qq0v68p.jpg?v=1wj5belaqv8",
  alt: "Soft morning light illuminating a woman in silk lingerie in a bright studio",
  type: "color",
  gridClass: "lg:col-span-2 lg:row-span-2",
  aspectClass: "aspect-[4/3] lg:aspect-auto lg:h-full",
  shopLink: "/HomePage"
}, {
  id: 2,
  title: "Midnight Noir",
  description: "Classic black and white portraiture that defines the XO Boudoir aesthetic. High-contrast lighting emphasizes the silhouette, creating a timeless image that speaks to strength and sophistication.",
  src: "https://warm-lionfish.demandflow.net/wp-content/uploads/2026/04/image_kusdpd1e.jpg?v=rg75krbf4lp",
  alt: "Dramatic black and white silhouette of a woman against a dark background",
  type: "bw",
  gridClass: "lg:col-span-1 lg:row-span-2",
  aspectClass: "aspect-[3/4] lg:aspect-auto lg:h-full",
  shopLink: "/HomePage"
}, {
  id: 3,
  title: "Lace & Shadows",
  description: "A detailed look at the intricate patterns of luxury lace. The interplay of shadow and fabric creates a mesmerizing depth, celebrating the artistry of high-end lingerie and fine art photography.",
  src: "https://warm-lionfish.demandflow.net/wp-content/uploads/2026/04/DSC01932.jpg?v=rdl3ieenw",
  alt: "Close-up detail of intricate black lace against soft skin",
  type: "color",
  gridClass: "lg:col-span-1 lg:row-span-1",
  aspectClass: "aspect-square",
  shopLink: "/HomePage"
}, {
  id: 4,
  title: "Grain & Grace",
  description: "Embracing the raw beauty of film-inspired texture. This artistic shot focuses on the organic drape of fabric, conveying a sense of movement and the tactile nature of the boudoir experience.",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1614369/img/boudoir_film_grain_fabric_flow.png",
  alt: "Artistic black and white shot with film grain showing the movement of sheer fabric",
  type: "bw",
  gridClass: "lg:col-span-1 lg:row-span-1",
  aspectClass: "aspect-square",
  shopLink: "/HomePage"
}, {
  id: 5,
  title: "Studio Accents",
  description: "The finishing touches that complete the narrative. From heirloom jewelry to curated studio pieces, every element is chosen to reflect your personal style and the luxury of the moment.",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1614369/img/boudoir_luxury_jewelry_heirloom.png",
  alt: "Elegant pearl necklace and vintage perfume bottle on a vanity",
  type: "color",
  gridClass: "lg:col-span-1 lg:row-span-1",
  aspectClass: "aspect-square",
  shopLink: "/HomePage"
}, {
  id: 6,
  title: "The Penthouse Series",
  description: "A wide-angle perspective on on-location luxury. Captured in a premier Minneapolis suite, this shot blends metropolitan elegance with personal intimacy, showcasing the grand scale of our sessions.",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1614369/img/boudoir_penthouse_suite_minneapolis.png",
  alt: "Wide view of a luxury penthouse suite with floor-to-ceiling windows and a plush bed",
  type: "color",
  gridClass: "lg:col-span-2 lg:row-span-1",
  aspectClass: "aspect-[16/9] lg:aspect-auto lg:h-full",
  shopLink: "/HomePage"
}, {
  id: 7,
  title: "Unapologetic",
  description: "A candid moment of pure confidence. This portrait captures the genuine laughter and empowerment that define the modern boudoir journey, rendered in classic, high-impact monochrome.",
  src: "https://warm-lionfish.demandflow.net/wp-content/uploads/2026/04/image_k18r98xr.jpg?v=ckz8wjezq1",
  alt: "Candid black and white portrait of a woman laughing joyfully",
  type: "bw",
  gridClass: "lg:col-span-1 lg:row-span-1",
  aspectClass: "aspect-square lg:aspect-auto lg:h-full",
  shopLink: "/HomePage"
}];
export default function ThePhotoshootSeries() {
  return <section data-section-id="68794" id="the-art-of-the-reveal" className="bg-white py-20 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {}
        <div className="mb-16 max-w-3xl mx-auto text-center space-y-6">
          <h2 className="text-4xl md:text-6xl font-serif font-normal tracking-tight text-black">
            The Art of the Reveal
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl font-sans font-light leading-relaxed tracking-wider">
            A curated exploration of intimacy and empowerment. Discover the quiet 
            moments and bold confidence captured in our signature Minneapolis studio sessions.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8 auto-rows-[300px] lg:auto-rows-[350px]">
          {galleryItems.map((item, i) => <Dialog key={item.id}>
              <DialogTrigger asChild>
                <div data-index={i} className={cn("group relative cursor-pointer overflow-hidden bg-[#fcfaf7] border-[20px] border-white shadow-sm transition-all duration-500 hover:shadow-xl", item.gridClass, item.aspectClass)}>
                  {}
                  <img src={item.src} alt={item.alt} className={cn("h-full w-full object-cover transition-all duration-700 ease-out group-hover:scale-105 group-hover:brightness-110", item.type === "bw" && "grayscale contrast-125")} />

                  {}
                  <div className="absolute inset-0 bg-black/0 transition-colors duration-500 group-hover:bg-white/10" />

                  {}
                  <div className="absolute inset-0 flex flex-col justify-end p-6 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                    <div className="translate-y-4 transform transition-transform duration-500 group-hover:translate-y-0 text-right">
                      <span className="inline-flex items-center gap-2 text-black font-sans font-medium tracking-widest text-[10px] uppercase mb-2">
                        <Maximize2 className="w-3 h-3" />
                        The Slow Reveal
                      </span>
                      <h3 className="text-black font-serif italic text-2xl">
                        {item.title}
                      </h3>
                    </div>
                  </div>
                </div>
              </DialogTrigger>

              {}
              <DialogContent className="max-w-5xl w-[95vw] p-0 gap-0 overflow-hidden border-none bg-white rounded-none">
                <div className="grid grid-cols-1 lg:grid-cols-2 h-[85vh] lg:h-[80vh]">
                  {}
                  <div className="relative h-1/2 lg:h-full w-full bg-[#fcfaf7] overflow-hidden">
                    <img src={item.src} alt={item.alt} className={cn("h-full w-full object-cover", item.type === "bw" && "grayscale contrast-125")} />
                  </div>

                  {}
                  <div className="flex flex-col justify-center p-8 lg:p-12 h-1/2 lg:h-full overflow-y-auto bg-white">
                    <DialogHeader className="text-left space-y-4 mb-8">
                      <div className="flex items-center gap-3 text-muted-foreground text-xs uppercase tracking-[0.2em] font-medium">
                        <span>Editorial</span>
                        <span className="w-1 h-1 rounded-full bg-border" />
                        <span>{item.type === "bw" ? "Noir" : "Color"}</span>
                      </div>
                      <DialogTitle className="text-3xl md:text-5xl font-serif font-normal text-black">
                        {item.title}
                      </DialogTitle>
                      <DialogDescription className="text-base md:text-lg text-muted-foreground leading-relaxed font-light pt-4 tracking-wide">
                        {item.description}
                      </DialogDescription>
                    </DialogHeader>

                    <div className="mt-auto pt-6 border-t border-border">
                      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                        <div className="space-y-1">
                          <p className="text-xs font-semibold text-black uppercase tracking-widest">
                            Session Note
                          </p>
                          <p className="text-xs text-muted-foreground italic">
                            Captured on location in Minneapolis
                          </p>
                        </div>
                        <Button asChild className="w-full sm:w-auto rounded-none h-12 px-8 text-sm uppercase tracking-widest bg-black text-white hover:bg-black/90">
                          <Link to={item.shopLink}>
                            <ShoppingBag className="mr-2 h-4 w-4" />
                            Book This Style
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
                
                {}
              </DialogContent>
            </Dialog>)}
        </div>

        {}
        <div className="mt-16 text-center">
          <Button asChild variant="outline" size="lg" className="rounded-none border-black text-black hover:bg-black hover:text-white transition-colors h-14 px-10 text-base font-medium uppercase tracking-[0.15em]">
            <Link to="/HomePage">
              View Full Portfolio <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}